/**
 * Shared API types and routes for Public API endpoints
 * These endpoints are accessible via public API keys (HMAC signed)
 */

// ============================================================================
// Public API Key Types
// ============================================================================

/**
 * Valid scopes for public API keys
 * - read_only: GET access to public API endpoints (tables, linked tables, OData)
 * - read_write: Same as read_only PLUS POST /api/public/tables/:ref/data (data insert)
 */
export type PublicApiKeyScope = 'read_only' | 'read_write';

/**
 * Public API key information returned to workspace admins
 */
export interface PublicApiKey {
  /** Unique identifier for the key */
  id: string;
  /** The plaintext API key (only returned on creation/regeneration) */
  key: string;
  /** Workspace this key belongs to */
  workspace_id: string;
  /** Key scope: 'read_only' or 'read_write' */
  scope: PublicApiKeyScope;
  /** When the key was created */
  created_at: Date | string;
  /** When the key expires (null = never) */
  expires_at: Date | string | null;
  /** Whether this is a newly created key */
  isNew?: boolean;
}

/**
 * Response from GET /api/private/public-api-keys
 * Returns both read_only and read_write keys (if they exist)
 */
export interface PublicApiKeyResponse {
  readKey: Omit<PublicApiKey, 'isNew'>;
  writeKey: Omit<PublicApiKey, 'isNew'> | null;
}

/**
 * Response from POST /api/private/public-api-keys/regenerate
 */
export interface PublicApiKeyRegenerateResponse {
  publicApiKey: Omit<PublicApiKey, 'isNew'>;
}

// ============================================================================
// Connector Types
// ============================================================================

/**
 * Sync mode for connector data ingestion
 */
export type SyncMode = 'replace' | 'append';

/**
 * Available connector types
 */
/** Note: 'email' is a planned connector type, not yet implemented (UI shows available: false) */
export type ConnectorType = 'nango' | 'http_fetch' | 'sql' | 'post_api' | 'email' | 'hubspot_direct';

/**
 * Base configuration shared by all connector types.
 */
export interface BaseConnectorConfig {
  sync_mode?: SyncMode;
  auto_tag_rotation?: boolean;
}

/**
 * Configuration for Nango-based connectors (third-party integrations)
 */
export interface NangoConnectorConfig extends BaseConnectorConfig {
  nango_connection_id: string;
  nango_provider_config_key: string;
  nango_model: string;
  nango_sync_name?: string;
  nango_last_cursor?: string | null;
}

/**
 * Authentication type for HTTP fetch connectors
 */
export type HttpFetchAuthType = 'none' | 'bearer' | 'basic' | 'api_key';

/**
 * Configuration for HTTP fetch connectors (REST API polling)
 */
export interface HttpFetchConnectorConfig extends BaseConnectorConfig {
  url: string;
  method: 'GET' | 'POST';
  headers?: Record<string, string>;
  body?: string;
  auth_type: HttpFetchAuthType;
  auth_value?: string;
  auth_header_name?: string;
  response_json_path?: string;
}

/**
 * Configuration for SQL fetch connectors (database polling)
 */
export interface SqlFetchConnectorConfig extends BaseConnectorConfig {
  connection_type: 'postgresql' | 'mysql';
  connection_string: string;
  query: string;
}

/**
 * Configuration for push API connectors (data received via POST endpoint)
 */
export type PostApiConnectorConfig = BaseConnectorConfig;

/**
 * HubSpot CRM object types supported by the direct connector.
 * Runtime array is the single source of truth; the type is derived from it.
 */
export const HUBSPOT_OBJECT_TYPES = [
  'contacts', 'companies', 'deals', 'tickets',
  'line_items', 'products', 'quotes',
  'calls', 'emails', 'meetings', 'notes', 'tasks',
  'owners', 'deal_pipelines',
] as const;

export type HubspotObjectType = (typeof HUBSPOT_OBJECT_TYPES)[number];

export interface IntegrationConnection {
  id: string;
  workspace_id: string;
  provider: string;
  external_id: string;
  display_name: string;
  created_at: string;
  updated_at: string;
}

/**
 * Connector types that support custom pull sync (HTTP Fetch / SQL / HubSpot Direct).
 */
export type CustomSyncConnectorType = 'http_fetch' | 'sql' | 'hubspot_direct';

/**
 * Configuration for HubSpot direct connectors (OAuth-based CRM data pull)
 */
export interface HubspotDirectConnectorConfig extends BaseConnectorConfig {
  connection_id: string; // FK to integration_connections.id
  object_type: HubspotObjectType;
  properties?: string[];
}

/**
 * Nango sync status information
 */
export interface NangoSyncStatus {
  name: string;
  status: string;
  frequency?: string | null;
  finishedAt?: string | null;
  nextScheduledSyncAt?: string | null;
  latestResult?: Record<string, unknown>;
}

/**
 * Request body for upserting a connector.
 */
export interface UpsertConnectorRequest {
  type: ConnectorType;
  config: Record<string, unknown>;
  schedule?: string | null;
}

/**
 * Serialized table connector returned to the frontend.
 * Config is sanitized (credentials redacted) before sending.
 */
export interface TableConnector {
  id: string;
  table_id: string;
  workspace_id: string;
  type: ConnectorType;
  config: NangoConnectorConfig | HttpFetchConnectorConfig | SqlFetchConnectorConfig | PostApiConnectorConfig | HubspotDirectConnectorConfig;
  schedule: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_synced_at: string | null;
  next_sync_at: string | null;
}

// ============================================================================
// Public Data Insert Types
// ============================================================================

/**
 * Response from POST /api/public/tables/:ref/data (data insert)
 */
export interface PublicDataInsertResponse {
  version_id: string;
  status: 'processing';
  records_submitted: number;
  poll_url: string;
  request_id?: string;
}

/**
 * Response from GET /api/public/tables/:ref/versions/:id/status (version polling)
 */
export interface PublicVersionStatusResponse {
  version_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress: number;
  records_processed: number;
  records_total: number;
  error_message: string | null;
  completed_at: string | null;
}

// ============================================================================
// Public API Routes
// ============================================================================

/**
 * Public API endpoints (read-only access via public API keys)
 */
export const PUBLIC_API_ROUTES = {
  tables: {
    /** GET - Get table version data as JSON */
    json: (tableRef: string, versionRef: string) =>
      `/api/public/tables/${tableRef}/versions/${versionRef}.json` as const,
    /** GET - Get table version data as CSV */
    csv: (tableRef: string, versionRef: string) =>
      `/api/public/tables/${tableRef}/versions/${versionRef}.csv` as const,
    /** GET - Get table version data as XLSX */
    xlsx: (tableRef: string, versionRef: string) =>
      `/api/public/tables/${tableRef}/versions/${versionRef}.xlsx` as const,
    /** GET - Get table version data as XML (Google Sheets compatible) */
    xml: (tableRef: string, versionRef: string) =>
      `/api/public/tables/${tableRef}/versions/${versionRef}.xml` as const,
  },
  linkedTables: {
    /** GET - Get linked table data as JSON */
    json: (linkedTableRef: string) =>
      `/api/public/linked-tables/${linkedTableRef}.json` as const,
    /** GET - Get linked table data as CSV */
    csv: (linkedTableRef: string) =>
      `/api/public/linked-tables/${linkedTableRef}.csv` as const,
    /** GET - Get linked table data as XLSX */
    xlsx: (linkedTableRef: string) =>
      `/api/public/linked-tables/${linkedTableRef}.xlsx` as const,
    /** GET - Get linked table data as XML (Google Sheets compatible) */
    xml: (linkedTableRef: string) =>
      `/api/public/linked-tables/${linkedTableRef}.xml` as const,
  },
  /** OData v4 endpoints for Power BI and Excel Power Query */
  odata: {
    /** Global OData - lists all tables in workspace */
    global: {
      /** GET - OData service document (lists all tables) */
      serviceDocument: '/api/public/odata/' as const,
      /** GET - OData service metadata document (EDMX) */
      metadata: '/api/public/odata/$metadata' as const,
      /** GET - List available tables */
      tables: '/api/public/odata/Tables' as const,
      /** GET - Query table data via OData */
      table: (tableRef: string) => `/api/public/odata/${tableRef}` as const,
    },
    /** Per-table OData - single entity set pattern with version in path (like linked tables) */
    perTable: {
      /** GET - OData service document for table version (returns single "data" entity set) */
      serviceDocument: (tableRef: string, versionRef: string) =>
        `/api/public/${tableRef}/odata/${versionRef}/` as const,
      /** GET - OData metadata for table version */
      metadata: (tableRef: string, versionRef: string) =>
        `/api/public/${tableRef}/odata/${versionRef}/$metadata` as const,
      /** GET - Query table version data */
      data: (tableRef: string, versionRef: string) =>
        `/api/public/${tableRef}/odata/${versionRef}/data` as const,
    },
    /** Linked table OData - single entity set with version selections in path */
    linkedTable: {
      /** GET - OData service document for linked table (current versions) */
      serviceDocument: (linkedTableRef: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/` as const,
      /** GET - OData service document with version config */
      serviceDocumentWithVersions: (linkedTableRef: string, versionConfig: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/@${versionConfig}/` as const,
      /** GET - OData metadata for linked table */
      metadata: (linkedTableRef: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/$metadata` as const,
      /** GET - OData metadata with version config */
      metadataWithVersions: (linkedTableRef: string, versionConfig: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/@${versionConfig}/$metadata` as const,
      /** GET - Query linked table data (current versions) */
      data: (linkedTableRef: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/data` as const,
      /** GET - Query linked table data with version config */
      dataWithVersions: (linkedTableRef: string, versionConfig: string) =>
        `/api/public/linked-tables/${linkedTableRef}/odata/@${versionConfig}/data` as const,
    },
    // Backwards compatibility aliases
    /** @deprecated Use odata.global.metadata */
    metadata: '/api/public/odata/$metadata' as const,
    /** @deprecated Use odata.global.tables */
    tables: '/api/public/odata/Tables' as const,
    /** @deprecated Use odata.global.table or odata.perTable.serviceDocument */
    table: (tableRef: string) => `/api/public/${tableRef}/odata/` as const,
  },
  /** POST - Insert data into a table via public API */
  dataInsert: (tableRef: string) =>
    `/api/public/tables/${tableRef}/data` as const,
  /** POST - Execute custom query */
  query: '/api/public/query',
} as const;

/** Type for public API routes */
export type PublicApiRoutes = typeof PUBLIC_API_ROUTES;
